/**
 * ProGate — reusable Pro tier enforcement components
 *
 * Exports:
 *   ProUpgradeBanner   — gradient banner with "Upgrade" CTA, shown to free users
 *   ProLockOverlay     — blur + lock icon overlay for locked metric cards / sections
 *   ProGate            — wrapper: renders children if Pro, overlay/fallback if not
 *
 * Usage:
 *
 *   // Wrap any Pro-only section
 *   <ProGate isPro={seekerUser?.is_pro}>
 *     <MySensitiveChart />
 *   </ProGate>
 *
 *   // Or with a custom fallback instead of the default overlay
 *   <ProGate isPro={seekerUser?.is_pro} fallback={<p>Upgrade to see this.</p>}>
 *     <MySensitiveChart />
 *   </ProGate>
 *
 *   // Show the banner at the top of a dashboard
 *   {!isPro && <ProUpgradeBanner />}
 *
 *   // Apply the lock overlay to a metric card manually
 *   <div className="relative overflow-hidden">
 *     {!isPro && <ProLockOverlay label="Salary benchmark" />}
 *     <ActualContent />
 *   </div>
 */

import { useNavigate } from "react-router-dom";

// ── Lock icon SVG ─────────────────────────────────────────────────────────────

function LockIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

// ── ProUpgradeBanner ──────────────────────────────────────────────────────────
//
// Gradient banner shown at the top of a Pro-gated page or section.
// Props:
//   title   — headline text (default: "Unlock full insights with Pro")
//   subtitle — body text (default: ghosting/salary/trends copy)

export function ProUpgradeBanner({
  title = "Unlock full insights with Pro",
  subtitle = "Ghost detection, salary benchmarks, application trends and more.",
}) {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl bg-brand-violet p-px mb-8">
      <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-6 py-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
            {title}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
        <button
          onClick={() => navigate("/pricing")}
          className="flex-shrink-0 bg-brand-violet text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition"
        >
          Upgrade
        </button>
      </div>
    </div>
  );
}

// ── ProLockOverlay ────────────────────────────────────────────────────────────
//
// Absolute-positioned blur overlay for a locked metric card or section.
// The parent must have `position: relative` and `overflow: hidden`.
//
// Props:
//   label   — optional description shown below the lock icon
//   size    — "sm" (metric card) | "md" (section). Defaults to "sm".

export function ProLockOverlay({ label, size = "sm" }) {
  const iconClass = size === "md" ? "w-6 h-6" : "w-5 h-5";
  const textClass = size === "md" ? "text-sm" : "text-xs";

  return (
    <div className="absolute inset-0 backdrop-blur-sm bg-white/60 dark:bg-brand-dark-surface/60 flex flex-col items-center justify-center z-10 rounded-2xl">
      <LockIcon className={`${iconClass} text-brand-violet dark:text-brand-violet-on-dark mb-1`} />
      {size === "md" ? (
        <>
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-1">
            Pro feature
          </p>
          {label && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          )}
        </>
      ) : (
        <span className={`${textClass} font-medium text-brand-violet dark:text-brand-violet-on-dark`}>
          {label || "Pro"}
        </span>
      )}
    </div>
  );
}

// ── ProGate ───────────────────────────────────────────────────────────────────
//
// Wrapper component. Renders children when isPro is true.
// When isPro is false, renders fallback if provided, otherwise a default
// overlay wrapper (blurred children + ProLockOverlay on top).
//
// Props:
//   isPro     — boolean from seekerUser?.is_pro
//   children  — Pro-only content
//   fallback  — optional custom fallback (replaces default overlay)
//   label     — label passed to ProLockOverlay when using default overlay
//   size      — size passed to ProLockOverlay ("sm" | "md")

export function ProGate({ isPro, children, fallback, label, size = "md" }) {
  if (isPro) return <>{children}</>;

  if (fallback) return <>{fallback}</>;

  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="pointer-events-none select-none opacity-40">
        {children}
      </div>
      <ProLockOverlay label={label} size={size} />
    </div>
  );
}

export default ProGate;
